import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import type { Workday } from '../../api/types';
import { Modal } from '../../components/Modal';
import { MapPreview } from '../../components/MapPreview';
import { AsyncButton } from '../../components/AsyncButton';
import { useToast } from '../../components/Toast';
import { fmtDateISO, fmtHM, fmtTime, todayISO, ymdLocal } from '../../utils/time';
import { computeDurationsClient } from '../../utils/workday';

function containsLocation(w: Workday, q: string) {
  const s = q.toLowerCase();

  const addrs = [
    w.start_work_loc?.address,
    w.start_break_loc?.address,
    w.end_break_loc?.address,
    w.end_work_loc?.address
  ].filter(Boolean).join(' ').toLowerCase();

  return addrs.includes(s);
}

function toLocalDateTime(iso: string | null) {
  if (!iso) return { date: '', time: '' };

  const d = new Date(iso);

  if (!Number.isFinite(d.getTime())) {
    return { date: '', time: '' };
  }

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');

  return {
    date: `${yyyy}-${mm}-${dd}`,
    time: `${hh}:${mi}`
  };
}

function fromLocalDateTime(date: string, time: string) {
  if (!date || !time) return null;

  const d = new Date(`${date}T${time}:00`);

  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

function addDaysISO(dateISO: string, days = 1) {
  const d = new Date(`${dateISO}T00:00:00`);

  d.setDate(d.getDate() + days);

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
}

function getDurationMin(w: Workday) {
  if (w.durations) {
    return {
      netMin: w.durations.net.totalMin,
      breakMin: w.durations.break.totalMin
    };
  }

  const d = computeDurationsClient(w, new Date());

  return {
    netMin: d.netMin,
    breakMin: d.breakMin
  };
}

function sameWorkdayDate(w: Workday, date: string) {
  return ymdLocal(w.work_date) === date;
}

export function HistoryPage() {
  const { push } = useToast();

  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);

    return d.toISOString().slice(0, 10);
  });

  const [to, setTo] = useState(() => todayISO());
  const [locQ, setLocQ] = useState('');
  const [items, setItems] = useState<Workday[]>([]);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Workday | null>(null);

  const [swDate, setSwDate] = useState('');
  const [swTime, setSwTime] = useState('');
  const [sbDate, setSbDate] = useState('');
  const [sbTime, setSbTime] = useState('');
  const [ebDate, setEbDate] = useState('');
  const [ebTime, setEbTime] = useState('');
  const [ewDate, setEwDate] = useState('');
  const [ewTime, setEwTime] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createDate, setCreateDate] = useState(() => todayISO());
  const [createStartWorkTime, setCreateStartWorkTime] = useState('');
  const [createStartBreakTime, setCreateStartBreakTime] = useState('');
  const [createEndBreakTime, setCreateEndBreakTime] = useState('');
  const [createEndWorkTime, setCreateEndWorkTime] = useState('');

  async function load() {
    try {
      const r = await api.get<{ items: Workday[] }>(
        `/api/me/workdays?from=${from}&to=${to}`
      );

      setItems(r.items || []);
    } catch (e: any) {
      push({
        title: 'Errore storico',
        body: e?.data?.message || e?.data?.error || e?.message
      });
    }
  }

  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!locQ.trim()) return items;

    return items.filter(w => containsLocation(w, locQ.trim()));
  }, [items, locQ]);

  const duplicateInLoadedItems = useMemo(() => {
    if (!createDate) return false;

    return items.some((w) => sameWorkdayDate(w, createDate));
  }, [items, createDate]);

  function openCreate() {
    setCreateDate(todayISO());
    setCreateStartWorkTime('');
    setCreateStartBreakTime('');
    setCreateEndBreakTime('');
    setCreateEndWorkTime('');
    setCreateOpen(true);
  }

  function openEdit(w: Workday) {
    setSelected(w);

    const a = toLocalDateTime(w.start_work_at);
    const b = toLocalDateTime(w.start_break_at);
    const c = toLocalDateTime(w.end_break_at);
    const d = toLocalDateTime(w.end_work_at);

    setSwDate(a.date || ymdLocal(w.work_date));
    setSwTime(a.time);

    setSbDate(b.date || ymdLocal(w.work_date));
    setSbTime(b.time);

    setEbDate(c.date || ymdLocal(w.work_date));
    setEbTime(c.time);

    setEwDate(d.date || ymdLocal(w.work_date));
    setEwTime(d.time);

    setOpen(true);
  }

  function validateTimes(args: {
    workDate: string;
    startWorkTime: string;
    startBreakTime: string;
    endBreakTime: string;
    endWorkTime: string;
  }) {
    const sw = fromLocalDateTime(args.workDate, args.startWorkTime);
    const sb = fromLocalDateTime(args.workDate, args.startBreakTime);
    const eb = fromLocalDateTime(args.workDate, args.endBreakTime);
    const ew = fromLocalDateTime(args.workDate, args.endWorkTime);

    if (!args.workDate) return 'Seleziona il giorno';
    if (!sw) return 'Inserisci l’orario di inizio lavoro';

    if ((sb && !eb) || (!sb && eb)) {
      return 'Per la pausa devi indicare sia inizio pausa sia fine pausa';
    }

    const swMs = new Date(sw).getTime();
    const sbMs = sb ? new Date(sb).getTime() : null;
    const ebMs = eb ? new Date(eb).getTime() : null;
    const ewMs = ew ? new Date(ew).getTime() : null;

    if (sbMs !== null && sbMs <= swMs) {
      return 'L’inizio pausa deve essere successivo all’inizio lavoro';
    }

    if (sbMs !== null && ebMs !== null && ebMs <= sbMs) {
      return 'La fine pausa deve essere successiva all’inizio pausa';
    }

    if (ewMs !== null && ewMs <= swMs) {
      return 'La fine lavoro deve essere successiva all’inizio lavoro';
    }

    if (ewMs !== null && sbMs !== null && sbMs >= ewMs) {
      return 'La pausa deve essere compresa tra inizio e fine lavoro';
    }

    if (ewMs !== null && ebMs !== null && ebMs > ewMs) {
      return 'La fine pausa non può essere successiva alla fine lavoro';
    }

    return null;
  }

  async function checkExistingWorkday(date: string) {
    const nextTo = addDaysISO(date);

    const r = await api.get<{ items: Workday[] }>(
      `/api/me/workdays?from=${date}&to=${nextTo}`
    );

    return r.items.find((w) => sameWorkdayDate(w, date)) || null;
  }

  async function saveCreate() {
    try {
      const validationError = validateTimes({
        workDate: createDate,
        startWorkTime: createStartWorkTime,
        startBreakTime: createStartBreakTime,
        endBreakTime: createEndBreakTime,
        endWorkTime: createEndWorkTime
      });

      if (validationError) {
        push({
          title: 'Dati non validi',
          body: validationError
        });

        return;
      }

      const existing = await checkExistingWorkday(createDate);

      if (existing) {
        push({
          title: 'Timbratura già presente',
          body: 'Per questo giorno esiste già una timbratura.'
        });

        return;
      }

      const r = await api.post<{ workday: Workday }>('/api/me/workdays', {
        work_date: createDate,
        start_work_at: fromLocalDateTime(createDate, createStartWorkTime),
        start_break_at: fromLocalDateTime(createDate, createStartBreakTime),
        end_break_at: fromLocalDateTime(createDate, createEndBreakTime),
        end_work_at: fromLocalDateTime(createDate, createEndWorkTime)
      });

      push({
        title: 'Giornata aggiunta'
      });

      setCreateOpen(false);

      await load();

      openEdit(r.workday);
    } catch (e: any) {
      push({
        title: 'Errore creazione',
        body: e?.data?.message || e?.data?.error || e?.message
      });
    }
  }

  async function saveEdit() {
    if (!selected) return;

    try {
      const workDate = ymdLocal(selected.work_date);

      const validationError = validateTimes({
        workDate,
        startWorkTime: swTime,
        startBreakTime: sbTime,
        endBreakTime: ebTime,
        endWorkTime: ewTime
      });

      if (validationError) {
        push({
          title: 'Dati non validi',
          body: validationError
        });

        return;
      }

      const r = await api.patch<{ workday: Workday }>(
        `/api/me/workdays/${selected._id}`,
        {
          start_work_at: fromLocalDateTime(workDate, swTime),
          start_break_at: fromLocalDateTime(workDate, sbTime),
          end_break_at: fromLocalDateTime(workDate, ebTime),
          end_work_at: fromLocalDateTime(workDate, ewTime)
        }
      );

      push({
        title: 'Timbratura aggiornata'
      });

      setSelected(r.workday);
      setOpen(false);

      await load();
    } catch (e: any) {
      push({
        title: 'Errore salvataggio',
        body: e?.data?.message || e?.data?.error || e?.message
      });
    }
  }

  return (
    <div className="grid" style={{ gap: 16, maxWidth: '100%', overflow: 'hidden' }}>
      <div className="card" style={{ maxWidth: '100%', overflow: 'hidden' }}>
        <div className="spread" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <div className="h2" style={{ marginTop: 0 }}>
              Storico
            </div>

            <div className="small">
              Consulta, aggiungi o modifica le tue giornate lavorate.
            </div>
          </div>

          <button
            className="btn btnSecondary"
            type="button"
            onClick={openCreate}
          >
            + Aggiungi giornata
          </button>
        </div>

        <div
          className="grid"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 12,
            marginTop: 14,
            maxWidth: '100%'
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div className="small">
              Da
            </div>

            <input
              className="input"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ minWidth: 0 }}>
            <div className="small">
              A
            </div>

            <input
              className="input"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        <div style={{ marginTop: 12, minWidth: 0 }}>
          <div className="small">
            Filtro località
          </div>

          <input
            className="input"
            placeholder="Es. Via Roma"
            value={locQ}
            onChange={(e) => setLocQ(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
        </div>

        <div className="row" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
          <AsyncButton
            className="btn btnPrimary"
            onClickAsync={load}
            busyText="Cerco…"
          >
            Cerca
          </AsyncButton>

          <div className="small">
            {filtered.length} risultati
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden', maxWidth: '100%' }}>
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--black)', color: 'var(--white)' }}>
                <th style={{ padding: 12, textAlign: 'left' }}>
                  Data
                </th>

                <th style={{ padding: 12, textAlign: 'left' }}>
                  Inizio
                </th>

                <th style={{ padding: 12, textAlign: 'left' }}>
                  Fine
                </th>

                <th style={{ padding: 12, textAlign: 'left' }}>
                  Netto
                </th>

                <th style={{ padding: 12, textAlign: 'left' }}>
                  Pausa
                </th>

                <th style={{ padding: 12 }} />
              </tr>
            </thead>

            <tbody>
              {filtered.map((w, idx) => {
                const dur = getDurationMin(w);

                return (
                  <tr
                    key={w._id}
                    style={{
                      background: idx % 2
                        ? 'rgba(255,255,255,.02)'
                        : 'rgba(255,255,255,.04)'
                    }}
                  >
                    <td style={{ padding: 12 }}>
                      {fmtDateISO(w.work_date)}
                    </td>

                    <td style={{ padding: 12 }}>
                      {fmtTime(w.start_work_at)}
                    </td>

                    <td style={{ padding: 12 }}>
                      {fmtTime(w.end_work_at)}
                    </td>

                    <td style={{ padding: 12, fontWeight: 800 }}>
                      {fmtHM(dur.netMin)}
                    </td>

                    <td style={{ padding: 12 }}>
                      {fmtHM(dur.breakMin)}
                    </td>

                    <td style={{ padding: 12, textAlign: 'right' }}>
                      <button
                        className="btn btnGhost"
                        type="button"
                        onClick={() => openEdit(w)}
                      >
                        Dettagli
                      </button>
                    </td>
                  </tr>
                );
              })}

              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="small" style={{ padding: 16 }}>
                    Nessun dato nel periodo.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Aggiungi giornata lavorata"
      >
        <div style={{ display: 'grid', gap: 14 }}>
          <div className="small">
            Inserisci manualmente una giornata lavorata. Se esiste già una timbratura per quel giorno,
            non potrai crearne un’altra.
          </div>

          <div>
            <div className="small">
              Giorno
            </div>

            <input
              className="input"
              type="date"
              value={createDate}
              onChange={(e) => setCreateDate(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          {duplicateInLoadedItems ? (
            <div
              className="card"
              style={{
                background: 'rgba(255, 180, 0, .12)',
                boxShadow: 'none',
                border: '1px solid rgba(255, 180, 0, .35)'
              }}
            >
              <b>
                Timbratura già presente
              </b>

              <div className="small" style={{ marginTop: 4 }}>
                Nella lista caricata esiste già una timbratura per questo giorno.
              </div>
            </div>
          ) : null}

          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 12
            }}
          >
            <ManualTimeField
              label="Inizio lavoro"
              required
              value={createStartWorkTime}
              onChange={setCreateStartWorkTime}
            />

            <ManualTimeField
              label="Inizio pausa"
              value={createStartBreakTime}
              onChange={setCreateStartBreakTime}
            />

            <ManualTimeField
              label="Fine pausa"
              value={createEndBreakTime}
              onChange={setCreateEndBreakTime}
            />

            <ManualTimeField
              label="Fine lavoro"
              value={createEndWorkTime}
              onChange={setCreateEndWorkTime}
            />
          </div>

          <div className="spread" style={{ gap: 10, flexWrap: 'wrap' }}>
            <button
              className="btn btnSecondary"
              type="button"
              onClick={() => setCreateOpen(false)}
            >
              Annulla
            </button>

            <AsyncButton
              className="btn btnPrimary"
              onClickAsync={saveCreate}
              busyText="Creo…"
              disabled={duplicateInLoadedItems || !createDate || !createStartWorkTime}
            >
              Aggiungi giornata
            </AsyncButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={selected ? `Dettagli e modifica · ${fmtDateISO(selected.work_date)}` : 'Dettagli'}
      >
        {selected ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="card" style={{ background: 'var(--gray-200)', boxShadow: 'none' }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span>
                  Netto
                </span>

                <b>
                  {fmtHM(getDurationMin(selected).netMin)}
                </b>
              </div>

              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span>
                  Pausa
                </span>

                <b>
                  {fmtHM(getDurationMin(selected).breakMin)}
                </b>
              </div>
            </div>

            <div className="grid" style={{ gap: 12 }}>
              <EditableEventRow
                label="Inizio lavoro"
                time={swTime}
                onTime={setSwTime}
                address={selected.start_work_loc?.address}
                map={selected.start_work_loc ? <MapPreview loc={selected.start_work_loc} /> : null}
              />

              <EditableEventRow
                label="Inizio pausa"
                time={sbTime}
                onTime={setSbTime}
                address={selected.start_break_loc?.address}
                map={selected.start_break_loc ? <MapPreview loc={selected.start_break_loc} /> : null}
              />

              <EditableEventRow
                label="Fine pausa"
                time={ebTime}
                onTime={setEbTime}
                address={selected.end_break_loc?.address}
                map={selected.end_break_loc ? <MapPreview loc={selected.end_break_loc} /> : null}
              />

              <EditableEventRow
                label="Fine lavoro"
                time={ewTime}
                onTime={setEwTime}
                address={selected.end_work_loc?.address}
                map={selected.end_work_loc ? <MapPreview loc={selected.end_work_loc} /> : null}
              />
            </div>

            <div className="small">
              Puoi modificare gli orari della giornata. Le posizioni originali restano visibili ma non vengono modificate.
            </div>

            <div className="spread" style={{ gap: 10, flexWrap: 'wrap' }}>
              <button
                className="btn btnSecondary"
                type="button"
                onClick={() => setOpen(false)}
              >
                Chiudi
              </button>

              <AsyncButton
                className="btn btnPrimary"
                onClickAsync={saveEdit}
                busyText="Salvo…"
              >
                Salva modifiche
              </AsyncButton>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function ManualTimeField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <div className="card" style={{ boxShadow: 'none', border: '1px solid var(--border)' }}>
      <div style={{ fontWeight: 900 }}>
        {props.label}
        {props.required ? ' *' : ''}
      </div>

      <div className="small" style={{ marginTop: 4 }}>
        Ora
      </div>

      <input
        className="input"
        type="time"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        style={{ marginTop: 6, width: '100%', boxSizing: 'border-box' }}
      />
    </div>
  );
}

function EditableEventRow(props: {
  label: string;
  time: string;
  onTime: (v: string) => void;
  address?: string;
  map?: React.ReactNode;
}) {
  return (
    <div className="card" style={{ boxShadow: 'none', border: '1px solid var(--border)' }}>
      <div className="spread" style={{ alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 900 }}>
            {props.label}
          </div>

          <div className="small" style={{ marginTop: 4 }}>
            {props.address || 'Posizione: —'}
          </div>
        </div>

        <div style={{ minWidth: 140 }}>
          <div className="small">
            Ora
          </div>

          <input
            className="input"
            type="time"
            value={props.time}
            onChange={(e) => props.onTime(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
        </div>
      </div>

      {props.map ? (
        <div style={{ marginTop: 10 }}>
          {props.map}
        </div>
      ) : null}
    </div>
  );
}